import { ApiResponse } from "../Types";
import {
  CourierAssignmentFilters,
  CourierAssignmentFormData,
  CourierFormData,
  CourierSettlementPayload,
} from "../Types/CourierTypes";
import { axiosClientv1 } from "./apiClient";

export const getCouriers = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get("/couriers");
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to load couriers.",
      data: [],
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const saveCourier = async (
  data: CourierFormData,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post("/couriers/save", data);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to save courier.",
      data: [],
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const deleteCourier = async (id: string): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post("/couriers/delete", { id });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to delete courier.",
      data: [],
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const assignCourierToOrder = async (
  data: CourierAssignmentFormData,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post("/couriers/assign", data);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to assign courier.",
      data: [],
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const updateCourierAssignmentStatus = async (data: {
  assignment_id: string;
  status: string;
  notes?: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post("/couriers/update-status", data);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to update courier status.",
      data: [],
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const updateCourierAssignmentFeePayer = async (data: {
  assignment_id: string;
  fee_payer: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post(
      "/couriers/update-fee-payer",
      data,
    );
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to update fee payer.",
      data: [],
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const getCourierAssignments = async (
  filters: CourierAssignmentFilters = {},
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get("/couriers/assignments", {
      params: filters,
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load courier assignments.",
      data: [],
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const unassignCourier = async (
  assignment_id: string,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post("/couriers/unassign", {
      assignment_id,
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to unassign courier.",
      data: [],
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const getCourierLedger = async (filters: {
  courier_id?: string;
  start_date?: string;
  end_date?: string;
  settled_status?: string;
  status?: string;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get("/couriers/ledger", {
      params: filters,
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load courier ledger.",
      data: {
        assignments: [],
        totals: {
          total_cod_receivable: 0,
          total_delivery_fee_payable: 0,
          net_receivable: 0,
        },
      },
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const settleCourierPayments = async (
  data: CourierSettlementPayload,
): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.post("/couriers/settle", data);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to settle courier payments.",
      data: [],
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};

export const getCourierPayments = async (filters: {
  courier_id?: string;
  page?: number;
  limit?: number;
}): Promise<ApiResponse<any>> => {
  try {
    const response = await axiosClientv1.get("/couriers/payments", {
      params: filters,
    });
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to load courier payments.",
      data: { payments: [], total: 0 },
      error: {
        code: error?.response?.status || 500,
        type: "server_error",
      },
    };
  }
};
